import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft,
  Save,
  Plus,
  X,
  Briefcase,
  FileText,
  Plane,
  Tag,
  Package,
  Scale,
  Shield,
  User,
  Building,
  MapPin,
  Headphones,
  Mail,
  ArrowRightLeft,
  Star,
  Ticket,
  Building2,
} from "lucide-react";
import RichHtmlEditor from "@/components/RichHtmlEditor";

const SettingsOpciones = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Cotizador inputs
  const [newBusinessUnit, setNewBusinessUnit] = useState("");
  const [newTripType, setNewTripType] = useState("");
  const [newCategory, setNewCategory] = useState("");
  
  // Artículos inputs
  const [newUnidad, setNewUnidad] = useState("");
  const [newAreaResp, setNewAreaResp] = useState("");
  const [newUbicacion, setNewUbicacion] = useState("");
  const [newDepartamento, setNewDepartamento] = useState("");
  const [activeTpl, setActiveTpl] = useState("new_ticket");
  
  const [settings, setSettings] = useState({
    business_units: ["Operaciones", "Marketing", "Aires Acondicionados"],
    trip_types: ["GRUPAL", "FIT", "INDIVIDUAL"],
    categories: ["MICE", "LEISURE"],
    proveedores: [],
    unidades: ["piezas", "litros", "kg", "cajas", "paquetes", "rollos", "bolsas"],
    areas_responsables: [],
    ubicaciones_logistica: [],
    articulos_catalogo: ["Trapeador", "Escoba", "Papel Higiénico", "Jabón Líquido", "Cloro", "Café", "Azúcar", "Agua", "Plumas", "Hojas Blancas", "Carpetas", "Grapas", "Clips"],
    responsables: [],
    traveldesk_calculadora_code: "",
    traveldesk_itinerarios_code: "",
    comercial_tabla_code: "",
    departamentos_operativos: [],
    helpdesk_email_templates: {},
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Merge with defaults for new fields
        setSettings(prev => ({
          ...prev,
          ...res.data,
          proveedores: res.data.proveedores || [],
          unidades: res.data.unidades || ["piezas", "litros", "kg", "cajas", "paquetes", "rollos", "bolsas"],
          areas_responsables: res.data.areas_responsables || [],
          ubicaciones_logistica: res.data.ubicaciones_logistica || [],
          articulos_catalogo: res.data.articulos_catalogo || ["Trapeador", "Escoba", "Papel Higiénico", "Jabón Líquido", "Cloro", "Café", "Azúcar", "Agua", "Plumas", "Hojas Blancas", "Carpetas", "Grapas", "Clips"],
          responsables: res.data.responsables || [],
          traveldesk_calculadora_code: res.data.traveldesk_calculadora_code || "",
          traveldesk_itinerarios_code: res.data.traveldesk_itinerarios_code || "",
          comercial_tabla_code: res.data.comercial_tabla_code || "",
          departamentos_operativos: res.data.departamentos_operativos || [],
          helpdesk_email_templates: res.data.helpdesk_email_templates || {},
        }));
      } catch (error) {
        console.error("Error fetching settings:", error);
        toast.error("Error al cargar configuración");
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [token]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Configuración guardada");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  // ============ COTIZADOR HANDLERS ============
  const addBusinessUnit = () => {
    if (newBusinessUnit.trim() && !settings.business_units?.includes(newBusinessUnit.trim())) {
      setSettings(prev => ({
        ...prev,
        business_units: [...(prev.business_units || []), newBusinessUnit.trim()]
      }));
      setNewBusinessUnit("");
    }
  };

  const removeBusinessUnit = (unit) => {
    setSettings(prev => ({
      ...prev,
      business_units: prev.business_units.filter(u => u !== unit)
    }));
  };

  const addTripType = () => {
    if (newTripType.trim() && !settings.trip_types?.includes(newTripType.trim().toUpperCase())) {
      setSettings(prev => ({
        ...prev,
        trip_types: [...(prev.trip_types || []), newTripType.trim().toUpperCase()]
      }));
      setNewTripType("");
    }
  };

  const removeTripType = (type) => {
    setSettings(prev => ({
      ...prev,
      trip_types: prev.trip_types.filter(t => t !== type)
    }));
  };

  const addCategory = () => {
    if (newCategory.trim() && !settings.categories?.includes(newCategory.trim().toUpperCase())) {
      setSettings(prev => ({
        ...prev,
        categories: [...(prev.categories || []), newCategory.trim().toUpperCase()]
      }));
      setNewCategory("");
    }
  };

  const removeCategory = (cat) => {
    setSettings(prev => ({
      ...prev,
      categories: prev.categories.filter(c => c !== cat)
    }));
  };

  // ============ ARTÍCULOS HANDLERS ============
  const addUnidad = () => {
    if (newUnidad.trim() && !settings.unidades?.includes(newUnidad.trim().toLowerCase())) {
      setSettings(prev => ({
        ...prev,
        unidades: [...(prev.unidades || []), newUnidad.trim().toLowerCase()]
      }));
      setNewUnidad("");
    }
  };

  const removeUnidad = (unidad) => {
    setSettings(prev => ({
      ...prev,
      unidades: prev.unidades.filter(u => u !== unidad)
    }));
  };

  // ============ ÁREAS RESPONSABLES HANDLERS ============
  const addAreaResp = () => {
    if (newAreaResp.trim() && !settings.areas_responsables?.includes(newAreaResp.trim())) {
      setSettings(prev => ({
        ...prev,
        areas_responsables: [...(prev.areas_responsables || []), newAreaResp.trim()]
      }));
      setNewAreaResp("");
    }
  };

  const removeAreaResp = (area) => {
    setSettings(prev => ({
      ...prev,
      areas_responsables: prev.areas_responsables.filter(a => a !== area)
    }));
  };

  // ============ UBICACIONES LOGÍSTICA HANDLERS ============
  const addUbicacion = () => {
    if (newUbicacion.trim() && !settings.ubicaciones_logistica?.includes(newUbicacion.trim())) {
      setSettings(prev => ({
        ...prev,
        ubicaciones_logistica: [...(prev.ubicaciones_logistica || []), newUbicacion.trim()]
      }));
      setNewUbicacion("");
    }
  };

  const removeUbicacion = (ubi) => {
    setSettings(prev => ({
      ...prev,
      ubicaciones_logistica: prev.ubicaciones_logistica.filter(u => u !== ubi)
    }));
  };


  if (loading) {
    return (
      <div className="p-4 md:p-6 lg:p-8 flex items-center justify-center min-h-screen">
        <div className="animate-pulse text-muted-foreground">Cargando configuración...</div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="settings-opciones-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/")}
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-heading text-3xl font-bold text-slate-900">
                Opciones
              </h1>
              <p className="text-slate-500">Configuración general del sistema</p>
            </div>
          </div>
          <Button
            className="action-button"
            onClick={handleSave}
            disabled={saving}
            data-testid="save-settings-button"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Guardando..." : "Guardar Cambios"}
          </Button>
        </div>

        <div className="space-y-10">
          {/* ==================== COTIZADOR SECTION ==================== */}
          <div>
            <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Cotizador
            </h2>
            <p className="text-sm text-slate-500 mb-4">
              Opciones configurables para los campos del cotizador
            </p>
            
            <div className="space-y-6 pl-4 border-l-2 border-blue-200">
              {/* Unidad de Negocio */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Briefcase className="w-5 h-5 text-slate-600" />
                    Unidad de Negocio (UENs)
                  </CardTitle>
                  <CardDescription>
                    Opciones para el campo "Área / Unidad de Negocio" en cotizaciones
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex flex-wrap gap-2">
                    {settings.business_units?.map((unit, index) => (
                      <Badge key={index} variant="secondary" className="flex items-center gap-1 px-3 py-1">
                        {unit}
                        <button type="button" onClick={() => removeBusinessUnit(unit)} className="ml-1 hover:text-red-500">
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                    {(!settings.business_units || settings.business_units.length === 0) && (
                      <span className="text-sm text-slate-400">No hay unidades configuradas</span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Nueva unidad de negocio..."
                      value={newBusinessUnit}
                      onChange={(e) => setNewBusinessUnit(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addBusinessUnit())}
                    />
                    <Button type="button" variant="outline" onClick={addBusinessUnit}>
                      <Plus className="w-4 h-4 mr-1" /> Agregar
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Tipo de Viaje */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Plane className="w-5 h-5 text-slate-600" />
                    Tipo de Viaje
                  </CardTitle>
                  <CardDescription>
                    Opciones para el campo "Tipo de Viaje" en cotizaciones
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex flex-wrap gap-2">
                    {settings.trip_types?.map((type, index) => (
                      <Badge key={index} variant="secondary" className="flex items-center gap-1 px-3 py-1">
                        {type}
                        <button type="button" onClick={() => removeTripType(type)} className="ml-1 hover:text-red-500">
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                    {(!settings.trip_types || settings.trip_types.length === 0) && (
                      <span className="text-sm text-slate-400">No hay tipos configurados</span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Nuevo tipo de viaje..."
                      value={newTripType}
                      onChange={(e) => setNewTripType(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addTripType())}
                    />
                    <Button type="button" variant="outline" onClick={addTripType}>
                      <Plus className="w-4 h-4 mr-1" /> Agregar
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Categoría */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Tag className="w-5 h-5 text-slate-600" />
                    Categoría
                  </CardTitle>
                  <CardDescription>
                    Opciones para el campo "Categoría" en cotizaciones
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex flex-wrap gap-2">
                    {settings.categories?.map((cat, index) => (
                      <Badge key={index} variant="secondary" className="flex items-center gap-1 px-3 py-1">
                        {cat}
                        <button type="button" onClick={() => removeCategory(cat)} className="ml-1 hover:text-red-500">
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                    {(!settings.categories || settings.categories.length === 0) && (
                      <span className="text-sm text-slate-400">No hay categorías configuradas</span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Nueva categoría..."
                      value={newCategory}
                      onChange={(e) => setNewCategory(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addCategory())}
                    />
                    <Button type="button" variant="outline" onClick={addCategory}>
                      <Plus className="w-4 h-4 mr-1" /> Agregar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* ==================== ARTÍCULOS SECTION ==================== */}
          <div>
            <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Package className="w-5 h-5 text-emerald-600" />
              Artículos (Logística)
            </h2>
            <p className="text-sm text-slate-500 mb-4">
              Opciones configurables para el módulo de inventario
            </p>
            
            <div className="space-y-6 pl-4 border-l-2 border-emerald-200">
              {/* Unidades */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Scale className="w-5 h-5 text-slate-600" />
                    Unidades de Medida
                  </CardTitle>
                  <CardDescription>
                    Unidades disponibles para medir artículos (piezas, litros, kg, etc.)
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex flex-wrap gap-2">
                    {settings.unidades?.map((unidad, index) => (
                      <Badge key={index} variant="secondary" className="flex items-center gap-1 px-3 py-1 bg-emerald-50">
                        {unidad}
                        <button type="button" onClick={() => removeUnidad(unidad)} className="ml-1 hover:text-red-500">
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                    {(!settings.unidades || settings.unidades.length === 0) && (
                      <span className="text-sm text-slate-400">No hay unidades configuradas</span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Nueva unidad (ej: galones)..."
                      value={newUnidad}
                      onChange={(e) => setNewUnidad(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addUnidad())}
                    />
                    <Button type="button" variant="outline" onClick={addUnidad}>
                      <Plus className="w-4 h-4 mr-1" /> Agregar
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Áreas Responsables */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Building className="w-5 h-5 text-slate-600" />
                    Áreas Responsables
                  </CardTitle>
                  <CardDescription>
                    Departamentos o áreas de la empresa para asignar a productos
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex flex-wrap gap-2">
                    {settings.areas_responsables?.map((area, index) => (
                      <Badge key={index} variant="secondary" className="flex items-center gap-1 px-3 py-1 bg-emerald-50">
                        {area}
                        <button type="button" onClick={() => removeAreaResp(area)} className="ml-1 hover:text-red-500">
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                    {(!settings.areas_responsables || settings.areas_responsables.length === 0) && (
                      <span className="text-sm text-slate-400">No hay áreas configuradas</span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Nueva área (ej: Administración)..."
                      value={newAreaResp}
                      onChange={(e) => setNewAreaResp(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addAreaResp())}
                    />
                    <Button type="button" variant="outline" onClick={addAreaResp}>
                      <Plus className="w-4 h-4 mr-1" /> Agregar
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Ubicaciones */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <MapPin className="w-5 h-5 text-slate-600" />
                    Ubicaciones
                  </CardTitle>
                  <CardDescription>
                    Ubicaciones físicas donde se almacenan los productos (bodegas, oficinas, etc.)
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex flex-wrap gap-2">
                    {settings.ubicaciones_logistica?.map((ubi, index) => (
                      <Badge key={index} variant="secondary" className="flex items-center gap-1 px-3 py-1 bg-emerald-50">
                        {ubi}
                        <button type="button" onClick={() => removeUbicacion(ubi)} className="ml-1 hover:text-red-500">
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                    {(!settings.ubicaciones_logistica || settings.ubicaciones_logistica.length === 0) && (
                      <span className="text-sm text-slate-400">No hay ubicaciones configuradas</span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Nueva ubicación (ej: Almacén Principal)..."
                      value={newUbicacion}
                      onChange={(e) => setNewUbicacion(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addUbicacion())}
                    />
                    <Button type="button" variant="outline" onClick={addUbicacion}>
                      <Plus className="w-4 h-4 mr-1" /> Agregar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
          <div>
            <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-indigo-600" />
              Empleados
            </h2>
            <p className="text-sm text-slate-500 mb-4">
              Opciones configurables para el módulo de empleados. Los departamentos se comparten con las Áreas Responsables de Logística.
            </p>
            
            <div className="space-y-6 pl-4 border-l-2 border-indigo-200">
              {/* Departamentos (espejo de Áreas Responsables) */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Building className="w-5 h-5 text-slate-600" />
                    Departamentos
                  </CardTitle>
                  <CardDescription>
                    Departamentos disponibles para asignar a empleados. Se sincronizan automáticamente con las Áreas Responsables de Logística.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex flex-wrap gap-2">
                    {settings.areas_responsables?.map((area, index) => (
                      <Badge key={index} variant="secondary" className="flex items-center gap-1 px-3 py-1 bg-indigo-50">
                        {area}
                        <button type="button" onClick={() => removeAreaResp(area)} className="ml-1 hover:text-red-500">
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                    {(!settings.areas_responsables || settings.areas_responsables.length === 0) && (
                      <span className="text-sm text-slate-400">No hay departamentos configurados</span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Nuevo departamento (ej: Recursos Humanos)..."
                      value={newAreaResp}
                      onChange={(e) => setNewAreaResp(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addAreaResp())}
                    />
                    <Button type="button" variant="outline" onClick={addAreaResp}>
                      <Plus className="w-4 h-4 mr-1" /> Agregar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-purple-600" />
              Activos Fijos (CARPEX)
            </h2>
            <p className="text-sm text-slate-500 mb-4">
              Los centros de trabajo y empleados se administran desde Configuración → Tablas
            </p>
          </div>

          {/* ==================== COMERCIAL SECTION ==================== */}
          <div>
            <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Plane className="w-5 h-5 text-blue-600" />
              Comercial
            </h2>
            <p className="text-sm text-slate-500 mb-4">
              Configura el código HTML/iframe para la Tabla Cotizadora
            </p>
            
            <div className="space-y-6 pl-4 border-l-2 border-blue-200">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    Tabla Cotizadora
                  </CardTitle>
                  <CardDescription>
                    Pega el código HTML o iframe completo. Se renderizará a pantalla completa en Comercial → Tabla Cotizadora.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <textarea
                    className="w-full min-h-[150px] p-3 font-mono text-sm border rounded-lg bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                    placeholder='<iframe src="https://tu-pagina.com/tabla" width="100%" height="100%" frameborder="0"></iframe>'
                    value={settings.comercial_tabla_code || ""}
                    onChange={(e) => setSettings(prev => ({ ...prev, comercial_tabla_code: e.target.value }))}
                    data-testid="comercial-tabla-code"
                  />
                  {settings.comercial_tabla_code && (
                    <p className="text-xs text-green-600">Código configurado ({settings.comercial_tabla_code.length} caracteres)</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          {/* ==================== TRAVELDESK SECTION ==================== */}
          <div>
            <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Plane className="w-5 h-5 text-cyan-600" />
              Traveldesk
            </h2>
            <p className="text-sm text-slate-500 mb-4">
              Configura el código HTML/iframe que se mostrará en cada página de Traveldesk
            </p>
            
            <div className="space-y-6 pl-4 border-l-2 border-cyan-200">
              {/* Calculadora de Precios: convertida a página nativa React
                  (Traveldesk → Calculadora de Precios). Ya no se configura por iframe. */}

              {/* Itinerarios */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    Itinerarios
                  </CardTitle>
                  <CardDescription>
                    Pega el código HTML o iframe completo. Se renderizará a pantalla completa en la sección Traveldesk → Itinerarios.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <textarea
                    className="w-full min-h-[150px] p-3 font-mono text-sm border rounded-lg bg-slate-50 focus:outline-none focus:ring-2 focus:ring-cyan-500 resize-y"
                    placeholder='<iframe src="https://tu-pagina.com/itinerarios" width="100%" height="100%" frameborder="0"></iframe>'
                    value={settings.traveldesk_itinerarios_code || ""}
                    onChange={(e) => setSettings(prev => ({ ...prev, traveldesk_itinerarios_code: e.target.value }))}
                    data-testid="traveldesk-itinerarios-code"
                  />
                  {settings.traveldesk_itinerarios_code && (
                    <p className="text-xs text-green-600">Código configurado ({settings.traveldesk_itinerarios_code.length} caracteres)</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          {/* ============ HELPDESK ============ */}
          <div>
            <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Headphones className="w-5 h-5 text-blue-700" />
              Helpdesk
            </h2>
            <p className="text-sm text-slate-500 mb-4">
              Configura los departamentos operativos y las plantillas HTML de correos automáticos.
            </p>

            <div className="space-y-6 pl-4 border-l-2 border-blue-200">
              {/* Plantillas de correo */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Mail className="w-4 h-4" />
                    Plantillas de correo automático
                  </CardTitle>
                  <CardDescription>
                    Personaliza los correos HTML que se envían ante cada evento del ticket. Usa las variables entre llaves dobles.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Tabs */}
                  <div className="flex flex-wrap gap-2 border-b">
                    {[
                      { k: "new_ticket", label: "Nuevo ticket", Icon: Ticket },
                      { k: "new_comment", label: "Nuevo comentario", Icon: Mail },
                      { k: "ticket_closed", label: "Ticket cerrado", Icon: Star },
                      { k: "ticket_transferred", label: "Transferencia", Icon: ArrowRightLeft },
                    ].map(({ k, label, Icon }) => (
                      <button
                        key={k}
                        type="button"
                        onClick={() => setActiveTpl(k)}
                        className={`flex items-center gap-2 px-4 py-2 text-sm rounded-t-lg border-b-2 transition-colors ${
                          activeTpl === k
                            ? "border-blue-600 text-blue-700 font-semibold bg-blue-50/50"
                            : "border-transparent text-slate-500 hover:text-slate-700"
                        }`}
                        data-testid={`tpl-tab-${k}`}
                      >
                        <Icon className="w-4 h-4" />
                        {label}
                      </button>
                    ))}
                  </div>

                  {/* Variables available */}
                  <div className="flex flex-wrap gap-1.5 bg-slate-50 rounded-lg p-3">
                    <span className="text-xs text-slate-500 mr-2 self-center">Variables:</span>
                    {["folio", "title", "status", "priority", "category", "departamento", "author_name", "comment_body", "ticket_url", "survey_url", "from_departamento", "to_departamento", "transfer_note"].map((v) => (
                      <code key={v} className="text-[11px] bg-white border px-1.5 py-0.5 rounded font-mono text-blue-700">
                        {`{{${v}}}`}
                      </code>
                    ))}
                  </div>

                  {/* Subject */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Asunto</label>
                    <Input
                      value={(settings.helpdesk_email_templates?.[activeTpl]?.subject) || ""}
                      onChange={(e) => setSettings((prev) => ({
                        ...prev,
                        helpdesk_email_templates: {
                          ...(prev.helpdesk_email_templates || {}),
                          [activeTpl]: {
                            ...((prev.helpdesk_email_templates || {})[activeTpl] || {}),
                            subject: e.target.value,
                          },
                        },
                      }))}
                      placeholder="[{{folio}}] Asunto del correo..."
                      data-testid={`tpl-subject-${activeTpl}`}
                    />
                  </div>

                  {/* Body editor */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Cuerpo HTML</label>
                    <RichHtmlEditor
                      value={(settings.helpdesk_email_templates?.[activeTpl]?.body_html) || ""}
                      onChange={(html) => setSettings((prev) => ({
                        ...prev,
                        helpdesk_email_templates: {
                          ...(prev.helpdesk_email_templates || {}),
                          [activeTpl]: {
                            ...((prev.helpdesk_email_templates || {})[activeTpl] || {}),
                            body_html: html,
                          },
                        },
                      }))}
                      data-testid={`tpl-body-${activeTpl}`}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsOpciones;
